import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  query,
  onSnapshot,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from '../images/hotel_logo.png';
import { HOTEL_NAME } from "../Variables.js";

Modal.setAppElement('#root');

// Date formatters
const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

const formatDateForFilename = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Natural sort for codes
const naturalSort = (a, b) => {
  const codeA = String(a.code ?? '').trim();
  const codeB = String(b.code ?? '').trim();
  return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
};

const Amenities = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [amenities, setAmenities] = useState([]);
  const [filteredAmenities, setFilteredAmenities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [newAmenity, setNewAmenity] = useState({
    name: '',
    code: '',
    description: '',
    price: 0,
    type: 'Complimentary',
    active: true
  });
  const [editingAmenity, setEditingAmenity] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [currentAmenity, setCurrentAmenity] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel');

  const amenitiesCollectionRef = collection(db, "amenities");
  const hotelName = `${HOTEL_NAME}`;

  // ==================== REAL-TIME LISTENER ====================
  useEffect(() => {
    if (!user?.uid || !userData) return;

    setIsFetching(true);

    const q = query(amenitiesCollectionRef);

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
        setAmenities(list);
        setHasFetched(true);
        setIsFetching(false);
      },
      (err) => {
        console.error("Error in amenities listener:", err);
        setError("Failed to load amenities");
        setShowModal(true);
        setIsFetching(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid, userData]);

  // Search filter
  useEffect(() => {
    const filtered = amenities.filter((amenity) =>
      amenity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (amenity.code && amenity.code.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredAmenities(filtered);
  }, [searchTerm, amenities]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAmenity({ ...newAmenity, [name]: value });
  };

  const resetForm = () => {
    setNewAmenity({
      name: '',
      code: '',
      description: '',
      price: 0,
      type: 'Complimentary',
      active: true
    });
  };

  const handleAddAmenity = async () => {
    if (!newAmenity.name.trim()) {
      setError("Amenity name is required");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(amenitiesCollectionRef, {
        ...newAmenity,
        price: Number(newAmenity.price),
        userId: user?.uid,
        createdAt: Timestamp.now()
      });

      setSuccessMessage("Amenity added successfully");
      resetForm();
      setIsAddModalOpen(false);
    } catch (err) {
      setError("Failed to add amenity");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateAmenity = async () => {
    setUpdate(true);
    try {
      const amenityDoc = doc(db, "amenities", editingAmenity.id);
      await updateDoc(amenityDoc, {
        ...newAmenity,
        price: Number(newAmenity.price)
      });

      setSuccessMessage("Amenity updated successfully");
      resetForm();
      setEditingAmenity(null);
      setIsEditModalOpen(false);
    } catch (err) {
      setError("Failed to update amenity");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleFileChange = (e) => setFile(e.target.files[0]);
  const handleFileTypeChange = (e) => setFileType(e.target.value);

  // ==================== OPTIMIZED BULK IMPORT ====================
  const processBulkImport = async () => {
    if (!file) {
      setError("Please select a file");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    setError("");
    setSuccessMessage("");

    try {
      let dataArray = [];
      let headers = [];

      if (fileType === 'excel') {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        headers = json[0].map(h => (h || '').toString().trim().toLowerCase());
        dataArray = json.slice(1);
      } else {
        const text = await file.text();
        const result = Papa.parse(text, { 
          header: true, 
          skipEmptyLines: true,
          transformHeader: h => h.trim().toLowerCase()
        });
        headers = Object.keys(result.data[0] || {}).map(h => h.toLowerCase());
        dataArray = result.data;
      }

      if (dataArray.length === 0) throw new Error("No data found in file");

      const batch = writeBatch(db);
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      const colMap = {
        name: headers.find(h => ['name', 'amenityname', 'title'].includes(h)),
        code: headers.find(h => ['code', 'amenitycode', 'number'].includes(h)),
        description: headers.find(h => ['description', 'desc'].includes(h)),
        price: headers.find(h => ['price', 'cost', 'amount'].includes(h)),
        type: headers.find(h => ['type', 'amenitytype', 'category'].includes(h)),
        active: headers.find(h => ['active', 'status'].includes(h))
      };

      console.log("Detected columns:", colMap); // Debug helper

      for (let i = 0; i < dataArray.length; i++) {
        const row = dataArray[i];

        const getValue = (key) => {
          if (fileType === 'excel') {
            const idx = headers.indexOf(colMap[key]);
            return idx !== -1 ? row[idx] : undefined;
          } else {
            return row[colMap[key]];
          }
        };

        const rawName = getValue('name');
        const nameStr = (rawName ?? '').toString().trim();

        if (!nameStr) {
          errorCount++;
          errors.push(`Row ${i + 2}: Missing amenity name`);
          continue;
        }

        const validTypes = ['Complimentary', 'Chargeable', 'Optional Extra'];
        const typeRaw = getValue('type') || 'Complimentary';
        const type = validTypes.includes(typeRaw) ? typeRaw : 'Complimentary';

        const amenityData = {
          name: nameStr,
          code: (getValue('code') ?? '').toString().trim(),
          description: (getValue('description') ?? '').toString().trim(),
          price: Number(getValue('price')) || 0,
          type,
          active: getValue('active') !== undefined 
            ? Boolean(getValue('active')) || 
              String(getValue('active')).toLowerCase() === 'true' ||
              String(getValue('active')).toLowerCase() === 'yes'
            : true,
          userId: user?.uid,
          createdAt: Timestamp.now()
        };

        const newDocRef = doc(amenitiesCollectionRef);
        batch.set(newDocRef, amenityData);
        successCount++;
      }

      if (successCount > 0) {
        await batch.commit();
      }

      let message = `Bulk import complete: ${successCount} amenities added`;
      if (errorCount > 0) message += ` • ${errorCount} skipped`;
      setSuccessMessage(message);

      if (errors.length > 0) console.warn("Import issues:", errors);

      setFile(null);
      setIsBulkImportModalOpen(false);

    } catch (err) {
      console.error("Bulk import error:", err);
      setError(`Import failed: ${err.message}`);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const generateAmenitiesPDF = () => {
    if (amenities.length === 0) {
      setError("No amenities to export");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF();
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";

    let y = 10;
    doc.addImage(Logo, "PNG", 14, y, 15, 15);
    doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.text(hotelName, 35, y + 5);
    doc.setFontSize(16); doc.setFont("helvetica", "normal"); doc.text("Amenities Report", 35, y + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, y + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, y + 23);
    y += 28;

    const sorted = [...amenities].sort(naturalSort);
    const tableData = sorted.map((amenity, i) => [
      (i + 1).toString(),
      amenity.code || '-',
      amenity.name,
      amenity.type,
      amenity.price > 0 ? `N${amenity.price.toLocaleString()}` : 'Free',
      amenity.active ? 'Active' : 'Inactive',
      amenity.createdAt ? dateFormatter.format(amenity.createdAt.toDate()) : '-'
    ]);

    autoTable(doc, {
      startY: y,
      head: [['S/N', 'Code', 'Name', 'Type', 'Price', 'Status', 'Created']],
      body: tableData,
      theme: 'striped',
      margin: { left: 8, right: 10 },
      styles: { fontSize: 8, textColor: 0 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 20 },
        2: { cellWidth: 55 },
        3: { cellWidth: 35 },
        4: { cellWidth: 30 },
        5: { cellWidth: 20 },
        6: { cellWidth: 25 }
      }
    });

    doc.save(`amenities_${formatDateForFilename(new Date())}.pdf`);
  };

  const handleEdit = (amenity) => {
    setEditingAmenity(amenity);
    setNewAmenity({
      name: amenity.name,
      code: amenity.code || '',
      description: amenity.description || '',
      price: amenity.price || 0,
      type: amenity.type || 'Complimentary',
      active: amenity.active
    });
    setIsEditModalOpen(true);
  };

  const confirmDeactivate = (amenity) => {
    setCurrentAmenity(amenity);
    setIsDeactivateModalOpen(true);
  };

  const handleDeactivate = async (id) => {
    setUpdate(true);
    try {
      await updateDoc(doc(db, "amenities", id), { active: false });
      setSuccessMessage("Amenity deactivated successfully");
    } catch (err) {
      setError("Failed to deactivate amenity");
    } finally {
      setUpdate(false);
      setIsDeactivateModalOpen(false);
      setShowModal(true);
    }
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Admin', 'Supreme', 'Front Desk', 'Cashier'];

  const displayContent = () => {
    if (isFetching && !hasFetched) return <p className="loading-text">Loading amenities...</p>;

    if (filteredAmenities.length === 0) {
      return searchTerm
        ? <p className="loading-text">No amenities match your search.</p>
        : <p className="loading-text">No amenities added yet. Add your first amenity or import in bulk.</p>;
    }

    return filteredAmenities.map((amenity) => (
      <div key={amenity.id} className="class-item">
        <div className="column gap-1">
          <p><strong>{amenity.name}</strong><br />
            <span>Code: {amenity.code || '—'}</span>&nbsp;-&nbsp;
            <span>Type: {amenity.type}</span><br />
            <span>Price: {amenity.price > 0 ? `₦${amenity.price.toLocaleString()}` : 'Complimentary'}</span><br />
            <span>{amenity.description || 'No description'}</span><br />
            <span className="size5 font-gold bold">{amenity.active ? 'Active' : 'Not active'}</span>
          </p>
        </div>
        <div className="row align-center justify-center gap-15">
          <button className="list-btn" onClick={() => handleEdit(amenity)}>
            <i className="fas fa-edit"></i>
          </button>
          {amenity.active && (
            <button className="list-btn delete-btn" onClick={() => confirmDeactivate(amenity)}>
              <i className="fas fa-ban"></i>
            </button>
          )}
        </div>
      </div>
    ));
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Amenities</h2>
            <Link to="#" className="home-link" onClick={() => window.location.reload()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed4">
            <div className="row justify-center align-center gap-15 add-buttons add-buttons1 margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Amenity</button>
              <button className="list-btn" onClick={generateAmenitiesPDF}><i className="fas fa-print"></i></button>
              <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>Bulk Import</button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by name or code"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => { setError(""); setSuccessMessage(""); setShowModal(false); }}
        />
      )}

      {/* Add Modal - unchanged */}
      <Modal isOpen={isAddModalOpen} onRequestClose={() => { setIsAddModalOpen(false); resetForm(); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Add Amenity</h2>
        <form className="session-form">
          <div className="desktop-form-input">
            <div>
              <label className="input-label">Name</label>
              <input type="text" name="name" className="input-field" placeholder="e.g., WiFi, Breakfast, Airport Transfer" value={newAmenity.name} onChange={handleInputChange} required />
            </div>
            <div>
              <label className="input-label">Code (Optional)</label>
              <input type="text" name="code" className="input-field" placeholder="e.g., WIFI, BRK" value={newAmenity.code} onChange={handleInputChange} />
            </div>
          </div>

          <label className="input-label">Description</label>
          <textarea name="description" className="input-field" rows="2" placeholder="Brief description" value={newAmenity.description} onChange={handleInputChange} />

          <div className="desktop-form-input">
            <div>
              <label className="input-label">Type</label>
              <select name="type" className="input-field" value={newAmenity.type} onChange={handleInputChange}>
                <option value="Complimentary">Complimentary (Free)</option>
                <option value="Chargeable">Chargeable (Fixed Price)</option>
                <option value="Optional Extra">Optional Extra</option>
              </select>
            </div>
            <div>
              <label className="input-label">Price (₦) - Set 0 for free</label>
              <input type="number" name="price" className="input-field" min="0" value={newAmenity.price} onChange={handleInputChange} />
            </div>
          </div>

          <div className="row align-center gap-1">
            <button type="button" className="login-button" onClick={() => { setIsAddModalOpen(false); resetForm(); }}>Cancel</button>
            <button type="button" className="login-button" onClick={handleAddAmenity}>Add Amenity</button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal - unchanged */}
      <Modal isOpen={isEditModalOpen} onRequestClose={() => { setIsEditModalOpen(false); resetForm(); setEditingAmenity(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Edit Amenity</h2>
        <form className="session-form">
          <div className="desktop-form-input">
            <div>
              <label className="input-label">Name</label>
              <input type="text" name="name" className="input-field" value={newAmenity.name} onChange={handleInputChange} required />
            </div>
            <div>
              <label className="input-label">Code</label>
              <input type="text" name="code" className="input-field" value={newAmenity.code} onChange={handleInputChange} />
            </div>
          </div>

          <label className="input-label">Description</label>
          <textarea name="description" className="input-field" rows="2" value={newAmenity.description} onChange={handleInputChange} />

          <div className="desktop-form-input">
            <div>
              <label className="input-label">Type</label>
              <select name="type" className="input-field" value={newAmenity.type} onChange={handleInputChange}>
                <option value="Complimentary">Complimentary (Free)</option>
                <option value="Chargeable">Chargeable (Fixed Price)</option>
                <option value="Optional Extra">Optional Extra</option>
              </select>
            </div>
            <div>
              <label className="input-label">Price (₦)</label>
              <input type="number" name="price" className="input-field" min="0" value={newAmenity.price} onChange={handleInputChange} />
            </div>
          </div>
          <label>
            <input type="checkbox" className='gold-tick' checked={newAmenity.active} onChange={(e) => setNewAmenity({ ...newAmenity, active: e.target.checked })} />
            Active
          </label>

          <div className="row align-center justify-center gap-1">
            <button type="button" className="login-button" onClick={() => { setIsEditModalOpen(false); resetForm(); setEditingAmenity(null); }}>Cancel</button>
            <button type="button" className="login-button" onClick={handleUpdateAmenity}>Update Amenity</button>
          </div>
        </form>
      </Modal>

      {/* Deactivate Modal - unchanged */}
      <Modal isOpen={isDeactivateModalOpen} onRequestClose={() => setIsDeactivateModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Deactivate Amenity</h2>
        {currentAmenity && (
          <div>
            <p>Are you sure you want to deactivate <strong>{currentAmenity.name}</strong>?</p>
            <div className="row align-center gap-1">
              <button className="login-button" onClick={() => setIsDeactivateModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" onClick={() => handleDeactivate(currentAmenity.id)}>Deactivate</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Import Modal - unchanged */}
      <Modal isOpen={isBulkImportModalOpen} onRequestClose={() => { setIsBulkImportModalOpen(false); setFile(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Bulk Import Amenities</h2>
        <div className="modal-form">
          <div style={{ marginBottom: '5px' }}>
            <label><input type="radio" name="fileType" value="excel" checked={fileType === 'excel'} onChange={handleFileTypeChange} /> Excel</label>&nbsp;&nbsp;
            <label><input type="radio" name="fileType" value="csv" checked={fileType === 'csv'} onChange={handleFileTypeChange} /> CSV</label>
          </div>

          <input type="file" accept={fileType === 'excel' ? '.xlsx,.xls' : '.csv'} onChange={handleFileChange} className="input-field1" />

          <div className="row align-center justify-center gap-15">
            <button className="login-button caps" onClick={() => { setIsBulkImportModalOpen(false); setFile(null); }}>Cancel</button>
            <button className="login-button caps" onClick={processBulkImport} disabled={!file || update}>Import</button>
          </div>

          <div className="import-instructions">
            <h4>Format Instructions:</h4>
            <p><strong>REQUIRED:</strong> name</p>
            <p><strong>OPTIONAL:</strong> code, description, price, type (Complimentary/Chargeable/Optional Extra), active</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Amenities;